import { arrayMove } from '@dnd-kit/sortable';
import { findArrayItemById } from '@hclinear/hc-linear-react';
import { t } from '@localization/i18n';
import {
  AnnouncementElementDTO,
  ElementDTOUnion,
  TextElementDTO,
} from '@model/element/ElementDTO';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Box } from '@type/layoutEditor/Box';
import {
  CanvasHistoryItem,
  CanvasHistoryItemType,
} from '@type/layoutEditor/CanvasHistory';
import { areBoxesEqual, boxMapper } from '@util/box.util';
import { applyDiff, objectDifference, objectIdNumberToString } from '@util/object.util';
import { v4 as uuidv4 } from 'uuid';
import {
  AnchorXDTO,
  AnchorYDTO,
  AnnouncementDTO,
  AnnouncementPreviewDTO,
  ColorDTO,
  ElementTypeDTO,
  ImageDTO,
  ImageResizeType,
  LayoutDTO,
  RasterFontDTO,
} from '@/model';
import { EditElementModalConfig } from '@/type';
import {
  firstArrayElement,
  insertAt,
  isArrayEmpty,
  modifyArrayItemById,
  sortArrayAsc,
  sortArrayDesc,
} from '@/util';

export interface LayoutEditorState {
  CANVAS_MAX_WIDTH?: number;
  CANVAS_MAX_HEIGHT?: number;

  width: number;
  height: number;

  SCALED_CANVAS_WIDTH: number;
  SCALED_CANVAS_HEIGHT: number;
  SCALE_FACTOR: number;

  TEXT_ELEMENT_STARTER_X: number;
  TEXT_ELEMENT_STARTER_Y: number;
  TEXT_ELEMENT_STARTER_WIDTH: number;
  TEXT_ELEMENT_STARTER_HEIGHT: number;

  ANNOUNCEMENT_ELEMENT_STARTER_X: number;
  ANNOUNCEMENT_ELEMENT_STARTER_Y: number;
  ANNOUNCEMENT_ELEMENT_STARTER_WIDTH: number;
  ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT: number;

  DRAWER_WIDTH: number;

  layoutId?: number;
  fetchedLayout?: LayoutDTO;
  fetchedElements?: ElementDTOUnion[];
  fetchedRasterFonts?: RasterFontDTO[];

  elements?: ElementDTOUnion[];

  unsavedElements: Set<number>;
  hiddenElements: Set<number>;

  selectedLayoutId: number | null;

  history: CanvasHistoryItem[];
  deletedItems: ElementDTOUnion[];

  elementToSave?: Partial<ElementDTOUnion>;
  fetchedAnnouncementPreviewElement?: AnnouncementPreviewDTO;
  fetchedImageElement?: ImageDTO;
  editElementModalConfig: EditElementModalConfig;

  isCanvasSaved: boolean;

  latestUpdate: number;

  tempIdConversions?: IdConversionType[];
}

const initialLayoutEditorState: LayoutEditorState = {
  SCALE_FACTOR: 2,

  SCALED_CANVAS_WIDTH: 0,
  SCALED_CANVAS_HEIGHT: 0,

  width: 0,
  height: 0,

  TEXT_ELEMENT_STARTER_X: 300,
  TEXT_ELEMENT_STARTER_Y: 700,
  TEXT_ELEMENT_STARTER_WIDTH: 800,
  TEXT_ELEMENT_STARTER_HEIGHT: 200,

  ANNOUNCEMENT_ELEMENT_STARTER_X: 0,
  ANNOUNCEMENT_ELEMENT_STARTER_Y: 1100,
  ANNOUNCEMENT_ELEMENT_STARTER_WIDTH: 800,
  ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT: 600,

  DRAWER_WIDTH: 400,

  selectedLayoutId: null,
  fetchedElements: [],

  elements: [],
  history: [],
  deletedItems: [],
  hiddenElements: new Set<number>(),
  unsavedElements: new Set<number>(),

  editElementModalConfig: { open: false, isExpanded: true },

  isCanvasSaved: true,

  latestUpdate: new Date().getTime(),
};

const LayoutEditorSlice = createSlice({
  name: 'LayoutEditor',
  initialState: initialLayoutEditorState,
  reducers: {
    setLayoutId: (state: LayoutEditorState, action: PayloadAction<number>): void => {
      state.layoutId = action.payload;
    },
    setFetchedLayout: (
      state: LayoutEditorState,
      action: PayloadAction<LayoutDTO>,
    ): void => {
      const fetchedLayout = action.payload;

      state.fetchedLayout = fetchedLayout;
      calculateScaledCanvasDimensions(
        state,
        fetchedLayout.displayWidth,
        fetchedLayout.displayHeight,
      );
      state.isCanvasSaved = true;
    },
    setFetchedRasterFonts: (
      state: LayoutEditorState,
      action: PayloadAction<RasterFontDTO[]>,
    ): void => {
      state.fetchedRasterFonts = action.payload;
    },
    setFetchedElements: (
      state: LayoutEditorState,
      action: PayloadAction<ElementDTOUnion[]>,
    ) => {
      const elements = action.payload;
      state.fetchedElements = elements;
      state.elements = sortArrayAsc(elements, 'prioritySn')?.map(objectIdNumberToString);
      actualizeHistoryWithSavedIds(state, elements);
      state.isCanvasSaved = true;
      state.unsavedElements = new Set();
    },
    resetToFetchedState: (state: LayoutEditorState) => {
      const { fetchedElements } = state;
      Object.assign(state, {
        ...state,
        elements: sortArrayAsc(fetchedElements, 'prioritySn')?.map(
          objectIdNumberToString,
        ),
        history: [],
        deletedItems: [],

        isCanvasSaved: true,
        hiddenElements: new Set(),
        unsavedElements: new Set(),
        tempIdConversions: null,
        selectedLayoutId: null,
      });
    },
    selectLayoutId(state: LayoutEditorState, action: PayloadAction<number>) {
      state.selectedLayoutId = action.payload;
      state.editElementModalConfig = { isExpanded: true, open: true };
    },
    toggleEditElementModalExpanded(state: LayoutEditorState) {
      state.editElementModalConfig = {
        ...state.editElementModalConfig,
        isExpanded: !state.editElementModalConfig.isExpanded,
      };
    },
    closeEditElementModal(state: LayoutEditorState) {
      state.editElementModalConfig = {
        open: false,
        element: null,
      };
    },
    initializeElement(
      state: LayoutEditorState,
      action: PayloadAction<Partial<ElementDTOUnion>>,
    ): void {
      const newElement: ElementDTOUnion = {
        id: getNextTempId(),
        layoutId: state.layoutId,
        prioritySn: state.elements.length + 1,
        name: 'test',
        x:
          action.payload.x ??
          Math.floor((state.fetchedLayout.displayWidth - action.payload.width) / 2),
        y:
          action.payload.y ??
          Math.floor((state.fetchedLayout.displayHeight - action.payload.height) / 2),
        backgroundColor: ColorDTO.FFF_White,
        ...buildDefaultAnchorsForElement(),
        ...action.payload,
      };

      state.elements.push(newElement);
      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_ADDED,
        id: newElement.id,
      });
      state.selectedLayoutId = newElement.id;
      state.unsavedElements.add(newElement.id);
    },
    initializeTextElement(
      state: LayoutEditorState,
      action: PayloadAction<ElementTypeDTO>,
    ): void {
      const { payload: elementType } = action;
      const {
        TEXT_ELEMENT_STARTER_WIDTH,
        TEXT_ELEMENT_STARTER_HEIGHT,
        fetchedRasterFonts,
      } = state;
      const uuid = uuidv4();
      const prioritySn = state.elements?.length + 1;

      const textValue = buildTextElementTextValue(elementType);

      const biggestFont = firstArrayElement(sortArrayDesc(fetchedRasterFonts, 'size'));

      const newElement: TextElementDTO = {
        id: getNextTempId(),
        textValue,
        layoutId: state.layoutId,
        prioritySn,
        name: uuid,
        x: Math.floor(
          (state.fetchedLayout.displayWidth - TEXT_ELEMENT_STARTER_WIDTH) / 2,
        ),
        y: Math.floor(
          (state.fetchedLayout.displayHeight - TEXT_ELEMENT_STARTER_HEIGHT) / 2,
        ),
        width: TEXT_ELEMENT_STARTER_WIDTH,
        height: TEXT_ELEMENT_STARTER_HEIGHT,
        contentWidth: TEXT_ELEMENT_STARTER_WIDTH,
        contentHeight: TEXT_ELEMENT_STARTER_HEIGHT,
        elementType,
        rasterFontId: biggestFont?.id,
        ttFontName: biggestFont?.ttFontName,
        ...buildDefaultColorsForElement(),
        ...buildDefaultAnchorsForElement(),
      };

      state.elements.push(newElement);
      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_ADDED,
        id: newElement.id,
      });
      state.selectedLayoutId = newElement.id;
      state.unsavedElements.add(newElement.id);
    },
    initializeAnnouncementElement(state: LayoutEditorState): void {
      const elementType = ElementTypeDTO.ANNOUNCEMENT;
      const {
        ANNOUNCEMENT_ELEMENT_STARTER_WIDTH,
        ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT,
        fetchedRasterFonts,
        fetchedLayout,
      } = state;
      const uuid = uuidv4();
      const prioritySn = state.elements?.length + 1;

      const announcementName = `${fetchedLayout.name}-${new Date().getTime()}`;

      const announcement: AnnouncementDTO = {
        name: announcementName,
        groupIds: fetchedLayout.groupIds,
        items: [],
      };

      const biggestFont = firstArrayElement(sortArrayDesc(fetchedRasterFonts, 'size'));

      const newElement: AnnouncementElementDTO = {
        id: getNextTempId(),
        announcement,
        layoutId: state.layoutId,
        prioritySn,
        name: uuid,
        x: Math.floor(
          (state.fetchedLayout.displayWidth - ANNOUNCEMENT_ELEMENT_STARTER_WIDTH) / 2,
        ),
        y: Math.floor(
          (state.fetchedLayout.displayHeight - ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT) / 2,
        ),
        width: ANNOUNCEMENT_ELEMENT_STARTER_WIDTH,
        height: ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT,
        contentWidth: ANNOUNCEMENT_ELEMENT_STARTER_WIDTH,
        contentHeight: ANNOUNCEMENT_ELEMENT_STARTER_HEIGHT,
        elementType,
        rasterFontId: biggestFont?.id,
        ttFontName: biggestFont?.ttFontName,
        imageResizeType: ImageResizeType.FIX,
        ...buildDefaultColorsForElement(),
        ...buildDefaultAnchorsForElement(),
      };
      state.elements.push(newElement);
      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_ADDED,
        id: newElement.id,
      });
      state.selectedLayoutId = newElement.id;
      state.unsavedElements.add(newElement.id);
    },
    setElementVisibility(
      state: LayoutEditorState,
      action: PayloadAction<{ id: number; visible: boolean }>,
    ) {
      const { id, visible } = action.payload;

      if (visible) {
        state.hiddenElements.delete(id);
      } else {
        state.hiddenElements.add(id);
      }
      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_VISIBILITY_CHANGED,
        id,
        visible: !visible,
      });
    },
    setFetchedImageElement(
      state: LayoutEditorState,
      action: PayloadAction<ImageDTO>,
    ): void {
      state.fetchedImageElement = action.payload;
    },
    reorderLayouts(
      state: LayoutEditorState,
      action: PayloadAction<{ oldIndex: number; newIndex: number }>,
    ) {
      const { oldIndex, newIndex } = action.payload;

      state.elements = arrayMove(state.elements, oldIndex, newIndex).map(
        (element, index) => ({
          ...element,
          prioritySn: index + 1,
        }),
      );

      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_ORDER_CHANGED,
        oldIndex,
        newIndex,
      });
    },
    updateElementPosition(
      state: LayoutEditorState,
      action: PayloadAction<Box & { id: number }>,
    ) {
      const element = findArrayItemById(state.elements, action.payload.id);

      const hasChanged = !areBoxesEqual(element, action.payload);

      if (hasChanged) {
        state.history.push({
          type: CanvasHistoryItemType.ELEMENT_CHANGE,
          id: action.payload.id,
          diff: boxMapper(element),
        });

        state.elements = modifyArrayItemById(state.elements, action.payload);

        state.unsavedElements.add(action.payload.id);
        notifyUpdate(state);

        state.isCanvasSaved = false;
      }
    },
    updateElementProperty(
      state: LayoutEditorState,
      action: PayloadAction<Partial<ElementDTOUnion> & { id: number }>,
    ) {
      const element = findArrayItemById(state.elements, action.payload.id);

      const diff = objectDifference(action.payload, element);

      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_CHANGE,
        id: action.payload.id,
        diff,
      });

      state.elements = modifyArrayItemById(state.elements, action.payload);

      state.unsavedElements.add(action.payload.id);
      notifyUpdate(state);
    },
    removeElement(state: LayoutEditorState, action: PayloadAction<number>) {
      const deleteId = action.payload;
      const element = findArrayItemById(state.elements, deleteId);
      const index = state.elements.indexOf(element);

      const updatedElements = state.elements.filter((element) => element.id !== deleteId);
      state.elements = calculatePrioritySn(updatedElements);
      state.deletedItems.push(element);
      state.history.push({
        type: CanvasHistoryItemType.ELEMENT_REMOVED,
        id: deleteId,
        index,
      });
      state.isCanvasSaved = false;
    },
    undoLastChange(state: LayoutEditorState): void {
      if (isArrayEmpty(state.history)) return;
      const historyItem = state.history.pop();
      switch (historyItem.type) {
        case CanvasHistoryItemType.ELEMENT_ADDED:
          state.elements = state.elements.filter((el) => el.id !== historyItem.id);
          break;
        case CanvasHistoryItemType.ELEMENT_VISIBILITY_CHANGED:
          if (historyItem.visible) {
            state.hiddenElements.delete(historyItem.id);
          } else {
            state.hiddenElements.add(historyItem.id);
          }
          break;
        case CanvasHistoryItemType.ELEMENT_CHANGE:
          const element = findArrayItemById(state.elements, historyItem.id);
          state.elements = modifyArrayItemById(state.elements, {
            id: historyItem.id,
            ...applyDiff(element, historyItem.diff),
          });
          break;
        case CanvasHistoryItemType.ELEMENT_ORDER_CHANGED:
          const { oldIndex, newIndex } = historyItem;

          state.elements = arrayMove(state.elements, newIndex, oldIndex).map(
            (element, index) => ({
              ...element,
              prioritySn: index + 1,
            }),
          );
          break;
        case CanvasHistoryItemType.ELEMENT_REMOVED:
          const { id, index } = historyItem;
          const removedElement = state.deletedItems.find((el) => el.id === id);

          state.elements = calculatePrioritySn(
            insertAt(state.elements, index, removedElement),
          );
          state.deletedItems = state.deletedItems.filter((el) => el.id !== id);
          break;
      }
      notifyUpdate(state);
    },
    buildTmpIdPrioritySnToHistoryUpdateMap(state: LayoutEditorState) {
      const { elements } = state;
      state.tempIdConversions = elements
        .filter((e) => e?.id < 0)
        .map((e) => ({
          tmpId: e.id,
          prioritySn: e.prioritySn,
        }));
    },
    resetState(state: LayoutEditorState) {
      Object.assign(state, initialLayoutEditorState);
    },
  },
});

const actualizeHistoryWithSavedIds = (
  state: LayoutEditorState,
  savedElements: ElementDTOUnion[],
): void => {
  const { tempIdConversions, history } = state;

  if (isArrayEmpty(tempIdConversions)) {
    return;
  }

  const tmpIdToSavedIdMap = new Map<string, string>();

  tempIdConversions.forEach(({ tmpId, prioritySn }) => {
    const saved = savedElements.find((el) => el.prioritySn === prioritySn);
    if (saved?.id) {
      tmpIdToSavedIdMap.set(String(tmpId), String(saved.id));
    }
  });

  state.history = history.map((h) => {
    if ('id' in h) {
      const newId = tmpIdToSavedIdMap.get(String(h.id));
      if (newId) {
        return { ...h, id: String(newId) };
      }
    }
    return h;
  });

  state.tempIdConversions = null;
};

const calculatePrioritySn = (elements: ElementDTOUnion[]) => {
  return [...elements].map((element, index) => ({
    ...element,
    prioritySn: index + 1,
  }));
};

const buildDefaultColorsForElement = () => ({
  fontColor: ColorDTO.FFF_White,
  backgroundColor: ColorDTO._000_Black,
});

const buildDefaultAnchorsForElement = () => ({
  anchorX: AnchorXDTO.LEFT,
  anchorY: AnchorYDTO.TOP,
});

const notifyUpdate = (state: LayoutEditorState) => {
  state.latestUpdate = new Date().getTime();
};

const calculateScaledCanvasDimensions = (
  state: LayoutEditorState,
  displayWidth: number,
  displayHeight: number,
) => {
  const { width, height } = state;

  const widthScale = width / displayWidth;
  const heightScale = height / displayHeight;
  const scaleFactor = Math.min(widthScale, heightScale, 1);

  state.SCALED_CANVAS_WIDTH = displayWidth;
  state.SCALED_CANVAS_HEIGHT = displayHeight;
  state.SCALE_FACTOR = scaleFactor;
};

const buildTextElementTextValue = (elementType: ElementTypeDTO): string => {
  switch (elementType) {
    case ElementTypeDTO.CLOCK:
      return '23:59';
    case ElementTypeDTO.STOP_NAME:
      return t('common.stop-name');
    case ElementTypeDTO.STOP_DIRECTION_NAME:
      return t('common.stop-direction-name');
    case ElementTypeDTO.PAGE_COUNTER:
      return t('layout-editor.timetable-page-counter.default-text');
    default:
      return t('common.text-element');
  }
};

let TEMP_ID_SEQUENCE = Number.MIN_SAFE_INTEGER;

function getNextTempId() {
  return TEMP_ID_SEQUENCE++;
}

export default LayoutEditorSlice.reducer;
export const layoutEditorActions = LayoutEditorSlice.actions;

type IdConversionType = {
  tmpId: number;
  prioritySn: number;
};
